import React, { useState } from "react";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaBell,
  FaMoon,
  FaSun,
  FaShieldAlt,
  FaTrash,
  FaCheckCircle,
  FaCamera,
  FaGlobe,
  FaToggleOn,
  FaToggleOff,
  FaWhatsapp,
  FaSms,
  FaSignOutAlt,
  FaExclamationTriangle,
  FaKey,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import api from "../../api/axiosClient";

function Toggle({ value, onChange, accent = "#fff000" }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 50,
        background: value ? accent : "#d1d5db",
        position: "relative",
        cursor: "pointer",
        transition: "background .25s",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 3,
          left: value ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#ffffff",
          boxShadow: "0 1px 4px rgba(0,0,0,.2)",
          transition: "left .25s cubic-bezier(.34,1.56,.64,1)",
        }}
      />
    </div>
  );
}

function Section({ icon, title, subtitle, children, danger }) {
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 18,
        border: danger ? "2px solid #fecaca" : "2px solid #f3f4f6",
        overflow: "hidden",
        marginBottom: "1.25rem",
      }}
    >
      <div
        style={{
          padding: "1.125rem 1.5rem",
          borderBottom: "2px solid #f9fafb",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: danger ? "#fff000" : "#ffffff",
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: danger ? "#fff000" : "#fff000",
            color: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: ".9rem",
            flexShrink: 0,
          }}
        >
          {icon}
        </span>

        <div>
          <div
            style={{
              fontWeight: 800,
              fontSize: ".9375rem",
              color: danger ? "#000000" : "#1c1917",
            }}
          >
            {title}
          </div>

          {subtitle && (
            <div
              style={{
                fontSize: ".75rem",
                color: "#9ca3af",
                marginTop: 1,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "1.25rem 1.5rem" }}>
        {children}
      </div>
    </div>
  );
}

function SettingRow({ label, sub, children, border = true }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        paddingBottom: border ? "1rem" : 0,
        marginBottom: border ? "1rem" : 0,
        borderBottom: border ? "1px solid #f5f5f4" : "none",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: ".9rem",
            color: "#1c1917",
          }}
        >
          {label}
        </div>

        {sub && (
          <div
            style={{
              fontSize: ".75rem",
              color: "#9ca3af",
              marginTop: 2,
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {children}
    </div>
  );
}

function SettingInput({
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        border: "2px solid #e5e7eb",
        borderRadius: 10,
        padding: ".625rem .875rem",
        fontSize: ".9rem",
        fontFamily: "'Raleway', sans-serif",
        color: "#1c1917",
        background: disabled ? "#f9fafb" : "#ffffff",
        width: "100%",
        transition: "border-color .2s",
        outline: "none",
      }}
      onFocus={(e) => {
        if (!disabled) {
          e.target.style.borderColor = "#fff000";
        }
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#e5e7eb";
      }}
    />
  );
}

function SettingsTab({ craftsman = {}, addToast }) {
  const [phone, setPhone] = useState(craftsman.phone || "");
  const [email, setEmail] = useState(craftsman.email || "");
  const [language, setLanguage] = useState(craftsman.language || "en");
  const [savingAccount, setSavingAccount] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const [notifs, setNotifs] = useState({
    newJob: true,
    jobAccepted: true,
    quoteApproved: true,
    payment: true,
    smsAlerts: false,
    whatsappAlerts: true,
    emailDigest: false,
  });

  const [savingNotifs, setSavingNotifs] = useState(false);

  const [theme, setTheme] = useState("light");
  const [compact, setCompact] = useState(false);

  const [deactivating, setDeactivating] = useState(false);

  const saveAccount = async () => {
    if (!phone && !email) {
      addToast?.(
        "Enter at least a phone or email.",
        "warning",
        "Missing Info"
      );
      return;
    }

    setSavingAccount(true);

    try {
      await api.patch("/craftsman/", {
        phone,
        email,
        language,
      });

      addToast?.(
        "Contact details updated.",
        "success",
        "Saved"
      );
    } catch {
      addToast?.(
        "Failed to save. Try again.",
        "error",
        "Error"
      );
    } finally {
      setSavingAccount(false);
    }
  };

  const savePassword = async () => {
    if (!currentPw || !newPw) {
      addToast?.(
        "Fill in all password fields.",
        "warning",
        "Missing Fields"
      );
      return;
    }

    if (newPw.length < 8) {
      addToast?.(
        "New password must be at least 8 characters.",
        "warning",
        "Too Short"
      );
      return;
    }

    if (newPw !== confirmPw) {
      addToast?.(
        "Passwords don't match.",
        "warning",
        "Mismatch"
      );
      return;
    }

    setSavingPw(true);

    try {
      await api.post("/auth/change-password/", {
        current_password: currentPw,
        new_password: newPw,
      });

      addToast?.(
        "Password changed successfully.",
        "success",
        "Password Updated"
      );

      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      addToast?.(
        e.response?.data?.detail ||
          "Incorrect current password.",
        "error",
        "Failed"
      );
    } finally {
      setSavingPw(false);
    }
  };

  const saveNotifs = async () => {
    setSavingNotifs(true);

    try {
      await api.patch("/craftsman/", {
        notification_settings: notifs,
      });

      addToast?.(
        "Notification preferences saved.",
        "success",
        "Saved"
      );
    } catch {
      addToast?.(
        "Failed to save preferences.",
        "error",
        "Error"
      );
    } finally {
      setSavingNotifs(false);
    }
  };

  const requestDeactivation = async () => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate your account? This hides your profile from clients."
      )
    ) {
      return;
    }

    setDeactivating(true);

    try {
      await api.post("/craftsman/deactivate/");

      addToast?.(
        "Account deactivation requested. Admin will review.",
        "warning",
        "Deactivation Requested",
        7000
      );
    } catch {
      addToast?.(
        "Failed to submit request.",
        "error",
        "Error"
      );
    } finally {
      setDeactivating(false);
    }
  };

  const PwField = ({
    value,
    onChange,
    placeholder,
  }) => (
    <div style={{ position: "relative" }}>
      <input
        type={showPw ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          border: "2px solid #e5e7eb",
          borderRadius: 10,
          padding: ".625rem 2.5rem .625rem .875rem",
          fontSize: ".9rem",
          fontFamily: "'Raleway', sans-serif",
          width: "100%",
          outline: "none",
          transition: "border-color .2s",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#fff000";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#e5e7eb";
        }}
      />

      <button
        type="button"
        onClick={() => setShowPw(!showPw)}
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#9ca3af",
          display: "flex",
          alignItems: "center",
        }}
      >
        {showPw ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );

  const SaveBtn = ({
    loading,
    onClick,
    children,
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      style={{
        background: "#fff000",
        border: "none",
        borderRadius: 10,
        color: "#000000",
        fontWeight: 800,
        fontSize: ".875rem",
        padding: ".625rem 1.375rem",
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.7 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontFamily: "'Raleway', sans-serif",
        transition: "all .2s",
      }}
    >
      {loading ? (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          style={{
            width: 14,
            height: 14,
            borderWidth: 2,
          }}
        />
      ) : (
        <FaCheckCircle size={13} />
      )}

      {loading ? "Saving…" : children}
    </button>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap');

        .st-wrap {
          font-family: 'Raleway', sans-serif;
          max-width: 720px;
        }

        .st-wrap * {
          box-sizing: border-box;
        }

        .st-tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: #fff000;
          color: #000000;
          border: 1.5px solid #fff000;
          border-radius: 50px;
          font-size: .72rem;
          font-weight: 700;
          padding: 3px 10px;
        }

        .lang-pill {
          padding: .5rem 1rem;
          border-radius: 50px;
          border: 2px solid #e5e7eb;
          background: #ffffff;
          color: #6b7280;
          font-weight: 700;
          font-size: .8rem;
          cursor: pointer;
          transition: all .2s;
          font-family: 'Raleway', sans-serif;
        }

        .lang-pill.active {
          background: #000000;
          color: #ffffff;
          border-color: #000000;
        }

        .theme-card {
          flex: 1;
          border: 2px solid #e5e7eb;
          border-radius: 14px;
          padding: 1rem;
          cursor: pointer;
          transition: all .2s;
          text-align: center;
          background: #ffffff;
        }

        .theme-card:hover {
          border-color: #d4cdc8;
        }

        .theme-card.active {
          border-color: #fff000;
          background: #ffffff;
        }

        .danger-btn {
          background: #ffffff;
          border: 2px solid #fca5a5;
          color: #ef4444;
          border-radius: 10px;
          padding: .625rem 1.25rem;
          font-weight: 700;
          font-size: .875rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-family: 'Raleway', sans-serif;
          transition: all .2s;
        }

        .danger-btn:hover {
          background: #fff000;
          color: #000000;
        }

        .st-avatar-wrap {
          position: relative;
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }

        .st-avatar {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          object-fit: cover;
          background: #f0fdf4;
          border: 3px solid #fff000;
        }

        .st-avatar-placeholder {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          background: #fff000;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          border: 3px solid #000000;
        }

        .st-avatar-edit {
          position: absolute;
          bottom: -4px;
          right: -4px;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: #000000;
          color: #ffffff;
          border: 2px solid #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: .65rem;
        }

        .st-avatar-edit:hover {
          background: #fff000;
          color: #000000;
        }

        @media (max-width: 576px) {
          .st-wrap {
            max-width: 100%;
          }

          .st-wrap > div {
            border-radius: 14px;
          }

          .theme-card {
            padding: .75rem .5rem;
          }
        }
      `}</style>

      <div className="st-wrap">

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1.25rem",
            marginBottom: "1.75rem",
            padding: "1.25rem 1.5rem",
            background: "#ffffff",
            borderRadius: 18,
            border: "2px solid #f3f4f6",
          }}
        >
          <div className="st-avatar-wrap">
            {craftsman.profile_url ? (
              <img
                src={craftsman.profile_url}
                className="st-avatar"
                alt="Profile"
              />
            ) : (
              <div className="st-avatar-placeholder">
                👷
              </div>
            )}

            <div className="st-avatar-edit">
              <FaCamera />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 900,
                fontSize: "1.25rem",
                color: "#1c1917",
                letterSpacing: "-.25px",
              }}
            >
              {craftsman.full_name ||
                craftsman.name ||
                "Your Name"}
            </div>

            <div
              style={{
                fontSize: ".8rem",
                color: "#9ca3af",
                marginTop: 2,
              }}
            >
              {craftsman.profession ||
                "Craftsman"}{" "}
              ·{" "}
              {craftsman.location ||
                "Location not set"}
            </div>

            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span className="st-tag">
                <FaCheckCircle size={9} />

                {craftsman.status === "approved"
                  ? "Verified"
                  : craftsman.status ||
                    "Pending"}
              </span>

              <span
                className="st-tag"
                style={{
                  background: "#fffbeb",
                  color: "#92400e",
                  borderColor: "#fde68a",
                }}
              >
                {craftsman.account_type ||
                  "Individual"}
              </span>
            </div>
          </div>
        </div>

        <Section
          icon={<FaUser />}
          title="Contact & Account"
          subtitle="Phone and email used for job notifications"
        >
          <div style={{ marginBottom: "1rem" }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: ".78rem",
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: .5,
                display: "block",
                marginBottom: 5,
              }}
            >
              Phone Number
            </label>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  background: "#f5f5f4",
                  border: "2px solid #e5e7eb",
                  borderRadius: 10,
                  padding: ".625rem .875rem",
                  color: "#6b7280",
                  fontWeight: 600,
                  fontSize: ".9rem",
                  whiteSpace: "nowrap",
                }}
              >
                🇰🇪 +254
              </span>

              <SettingInput
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value)
                }
                placeholder="7XX XXX XXX"
                type="tel"
              />
            </div>
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: ".78rem",
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: .5,
                display: "block",
                marginBottom: 5,
              }}
            >
              Email Address
            </label>

            <SettingInput
              value={email}
              onChange={(e) =>
                setEmail(e.target.value)
              }
              placeholder="you@example.com"
              type="email"
            />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: ".78rem",
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: .5,
                display: "block",
                marginBottom: 8,
              }}
            >
              Language
            </label>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {[
                { id: "en", label: "English" },
                { id: "sw", label: "Kiswahili" },
              ].map((l) => (
                <button
                  type="button"
                  key={l.id}
                  className={`lang-pill ${
                    language === l.id
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    setLanguage(l.id)
                  }
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <SaveBtn
            loading={savingAccount}
            onClick={saveAccount}
          >
            Save Contact Info
          </SaveBtn>
        </Section>

        <Section
          icon={<FaShieldAlt />}
          title="Password & Security"
          subtitle="Change your login password"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: ".875rem",
              marginBottom: "1.25rem",
            }}
          >
            <div>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: ".78rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: .5,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Current Password
              </label>

              <PwField
                value={currentPw}
                onChange={(e) =>
                  setCurrentPw(e.target.value)
                }
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: ".78rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: .5,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                New Password
              </label>

              <PwField
                value={newPw}
                onChange={(e) =>
                  setNewPw(e.target.value)
                }
                placeholder="Min. 8 characters"
              />
            </div>

            <div>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: ".78rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: .5,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Confirm New Password
              </label>

              <PwField
                value={confirmPw}
                onChange={(e) =>
                  setConfirmPw(e.target.value)
                }
                placeholder="Repeat new password"
              />
            </div>
          </div>

          {newPw &&
            confirmPw &&
            newPw !== confirmPw && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1.5px solid #fca5a5",
                  borderRadius: 10,
                  padding: ".625rem 1rem",
                  fontSize: ".8rem",
                  color: "#ef4444",
                  fontWeight: 600,
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <FaExclamationTriangle size={12} />
                Passwords don't match
              </div>
            )}

          {newPw &&
            newPw.length >= 8 &&
            newPw === confirmPw && (
              <div
                style={{
                  background: "#fff000",
                  border: "1.5px solid #000000",
                  borderRadius: 10,
                  padding: ".625rem 1rem",
                  fontSize: ".8rem",
                  color: "#000000",
                  fontWeight: 600,
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <FaCheckCircle size={12} />
                Passwords match
              </div>
            )}

          <SaveBtn
            loading={savingPw}
            onClick={savePassword}
          >
            Update Password
          </SaveBtn>
        </Section>

        <Section
          icon={<FaBell />}
          title="Notifications"
          subtitle="Control when and how you get alerted"
        >
          <div
            style={{
              marginBottom: "1rem",
              paddingBottom: "1rem",
              borderBottom: "2px solid #f5f5f4",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: ".72rem",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: .8,
                marginBottom: ".75rem",
              }}
            >
              Job Alerts
            </div>

            {[
              {
                key: "newJob",
                label: "New job assigned",
                sub: "When admin assigns a job to you",
              },
              {
                key: "jobAccepted",
                label: "Quote activity",
                sub: "When client approves or rejects your quote",
              },
              {
                key: "quoteApproved",
                label: "Job status changes",
                sub: "In-progress, completed confirmations",
              },
              {
                key: "payment",
                label: "Payment notifications",
                sub: "When payment is confirmed",
              },
            ].map((n) => (
              <SettingRow
                key={n.key}
                label={n.label}
                sub={n.sub}
              >
                <Toggle
                  value={notifs[n.key]}
                  onChange={(v) =>
                    setNotifs({
                      ...notifs,
                      [n.key]: v,
                    })
                  }
                />
              </SettingRow>
            ))}
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: ".72rem",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: .8,
                marginBottom: ".75rem",
              }}
            >
              Delivery Channels
            </div>

            {[
              {
                key: "smsAlerts",
                label: "SMS Alerts",
                sub: "Texts to your phone number",
                icon: <FaSms />,
              },
              {
                key: "whatsappAlerts",
                label: "WhatsApp Notifications",
                sub: "Messages to your WhatsApp",
                icon: <FaWhatsapp />,
              },
              {
                key: "emailDigest",
                label: "Weekly Email Digest",
                sub: "Summary of your week's activity",
                icon: <FaEnvelope />,
              },
            ].map((n, i, arr) => (
              <SettingRow
                key={n.key}
                label={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    {n.icon}
                    {n.label}
                  </span>
                }
                sub={n.sub}
                border={i < arr.length - 1}
              >
                <Toggle
                  value={notifs[n.key]}
                  onChange={(v) =>
                    setNotifs({
                      ...notifs,
                      [n.key]: v,
                    })
                  }
                />
              </SettingRow>
            ))}
          </div>

          <SaveBtn
            loading={savingNotifs}
            onClick={saveNotifs}
          >
            Save Preferences
          </SaveBtn>
        </Section>

        <Section
          icon={<FaSun />}
          title="Appearance"
          subtitle="Personalise how the dashboard looks"
        >
          <div style={{ marginBottom: "1.25rem" }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: ".78rem",
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: .5,
                marginBottom: ".875rem",
              }}
            >
              Theme
            </div>

            <div
              style={{
                display: "flex",
                gap: ".75rem",
              }}
            >
              {[
                {
                  id: "light",
                  icon: <FaSun size={20} />,
                  label: "Light",
                },
                {
                  id: "dark",
                  icon: <FaMoon size={20} />,
                  label: "Dark",
                },
                {
                  id: "auto",
                  icon: (
                    <span
                      style={{
                        fontSize: "1.25rem",
                      }}
                    >
                      🖥️
                    </span>
                  ),
                  label: "System",
                },
              ].map((t) => (
                <div
                  key={t.id}
                  className={`theme-card ${
                    theme === t.id ? "active" : ""
                  }`}
                  onClick={() => setTheme(t.id)}
                >
                  <div style={{ marginBottom: 6 }}>
                    {t.icon}
                  </div>

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: ".8rem",
                      color:
                        theme === t.id
                          ? "#000000"
                          : "#6b7280",
                    }}
                  >
                    {t.label}
                  </div>

                  {theme === t.id && (
                    <div
                      style={{
                        marginTop: 4,
                        color: "#000000",
                        fontSize: ".65rem",
                        fontWeight: 800,
                      }}
                    >
                      ✓ ACTIVE
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <SettingRow
            label="Compact Mode"
            sub="Reduce padding for a denser layout"
            border={false}
          >
            <Toggle
              value={compact}
              onChange={setCompact}
            />
          </SettingRow>

          <div
            style={{
              marginTop: "1rem",
              background: "#fafaf9",
              borderRadius: 10,
              padding: ".875rem",
              fontSize: ".78rem",
              color: "#9ca3af",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <FaExclamationTriangle
              size={11}
              color="#fff000"
            />

            Dark mode and compact mode will be fully
            applied in a future update. Changes are saved.
          </div>
        </Section>

        <Section
          icon={<FaExclamationTriangle />}
          title="Danger Zone"
          subtitle="Irreversible account actions"
          danger
        >
          <SettingRow
            label="Deactivate Account"
            sub="Hides your profile from clients. You can reactivate by contacting support."
            border={false}
          >
            <button
              onClick={requestDeactivation}
              disabled={deactivating}
              style={{
                background: "#ffffff",
                border: "2px solid #fca5a5",
                color: "#ef4444",
                borderRadius: 10,
                padding: ".5rem 1.125rem",
                fontWeight: 700,
                fontSize: ".85rem",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontFamily: "'Raleway', sans-serif",
                transition: "all .2s",
                opacity: deactivating ? 0.7 : 1,
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fff000";
                e.currentTarget.style.color = "#000000";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = "#ef4444";
              }}
            >
              {deactivating ? (
                "Requesting…"
              ) : (
                <>
                  <FaSignOutAlt size={13} />
                  Deactivate
                </>
              )}
            </button>
          </SettingRow>
        </Section>

        <div
          style={{
            textAlign: "center",
            padding: "1rem 0 .5rem",
            color: "#d1d5db",
            fontSize: ".72rem",
            fontWeight: 600,
          }}
        >
          Kaakazini Craftsman App · v1.0.0 ·{" "}
          <a
            href="/privacy"
            style={{ color: "#d1d5db" }}
          >
            Privacy
          </a>{" "}
          ·{" "}
          <a
            href="/terms"
            style={{ color: "#d1d5db" }}
          >
            Terms
          </a>
        </div>
      </div>
    </>
  );
}

export default SettingsTab;

